'use client';

import { c as _c } from "react/compiler-runtime";
import { hexToHsv, hsvToHex, isValidHex } from '@jaaq/utils';
import { Pipette, X } from 'lucide-react';
import type * as React from 'react';
import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { HStack } from './h-stack';
type ColorPickerPopoverProps = {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  onClose: () => void;
};

/** A saturation/brightness + hue colour picker popover; closes on outside click or Escape. */
export const ColorPickerPopover = t0 => {
  const $ = _c(61);
  const {
    label,
    value,
    onChange,
    onClose
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = isValidHex(value) ? hexToHsv(value) : {
      h: 210,
      s: 0.37,
      v: 0.93
    };
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const init = t1;
  const [hue, setHue] = useState(init.h);
  const [sat, setSat] = useState(init.s);
  const [val, setVal] = useState(init.v);
  const [hexInput, setHexInput] = useState(value);
  const gradRef = useRef(null);
  const popoverRef = useRef(null);
  let t2;
  let t3;
  if ($[2] !== onClose) {
    t2 = () => {
      const onDown = function onDown(event) {
        if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
          onClose();
        }
      };
      const onKey = function onKey(event_0) {
        if (event_0.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("mousedown", onDown);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onDown);
        document.removeEventListener("keydown", onKey);
      };
    };
    t3 = [onClose];
    $[2] = onClose;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== onChange) {
    t4 = (hueValue, saturationValue, brightnessValue) => {
      const hex = hsvToHex(hueValue, saturationValue, brightnessValue);
      setHexInput(hex);
      onChange(hex);
    };
    $[5] = onChange;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const commit = t4;
  let t5;
  if ($[7] !== commit || $[8] !== hue) {
    t5 = event_1 => {
      const rect = gradRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const newS = Math.max(0, Math.min(1, (event_1.clientX - rect.left) / rect.width));
      const newV = 1 - Math.max(0, Math.min(1, (event_1.clientY - rect.top) / rect.height));
      setSat(newS);
      setVal(newV);
      commit(hue, newS, newV);
    };
    $[7] = commit;
    $[8] = hue;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const onGradientPointer = t5;
  let t6;
  if ($[10] !== label) {
    t6 = <span className="text-sm font-semibold text-fg">{label}</span>;
    $[10] = label;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <X className="size-3.5 text-fg" strokeWidth={1.5} />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== onClose) {
    t8 = <button type="button" onClick={onClose} className="size-6 flex items-center justify-center rounded-full hover:bg-surface-muted transition-colors" aria-label="Close">{t7}</button>;
    $[13] = onClose;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6 || $[16] !== t8) {
    t9 = <HStack align="center" justify="between" className="px-4 py-3 border-b border-border">{t6}{t8}</HStack>;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== hue) {
    t10 = hsvToHex(hue, 1, 1);
    $[18] = hue;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = `linear-gradient(to right, #fff, ${t10})`;
  let t12;
  if ($[20] !== t11) {
    t12 = {
      background: t11
    };
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  let t14;
  if ($[22] !== onGradientPointer) {
    t13 = event_2 => {
      event_2.currentTarget.setPointerCapture(event_2.pointerId);
      onGradientPointer(event_2);
    };
    t14 = event_3 => {
      if (event_3.buttons === 1) {
        onGradientPointer(event_3);
      }
    };
    $[22] = onGradientPointer;
    $[23] = t13;
    $[24] = t14;
  } else {
    t13 = $[23];
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="absolute inset-0" style={{
      background: "linear-gradient(to bottom, transparent, #000)"
    }} />;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const t16 = `${sat * 100}%`;
  const t17 = `${(1 - val) * 100}%`;
  let t18;
  if ($[26] !== hue || $[27] !== sat || $[28] !== val) {
    t18 = hsvToHex(hue, sat, val);
    $[26] = hue;
    $[27] = sat;
    $[28] = val;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t16 || $[31] !== t17 || $[32] !== t18) {
    t19 = <div className="absolute size-4 rounded-full border-2 border-fg-inverse shadow-md -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{
      left: t16,
      top: t17,
      backgroundColor: t18
    }} />;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t12 || $[35] !== t13 || $[36] !== t14 || $[37] !== t19) {
    t20 = <div ref={gradRef} className="relative w-full h-45 cursor-crosshair select-none" style={t12} onPointerDown={t13} onPointerMove={t14}>{t15}{t19}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== commit || $[40] !== sat || $[41] !== val) {
    t21 = event_4 => {
      const nextHue = Number(event_4.target.value);
      setHue(nextHue);
      commit(nextHue, sat, val);
    };
    $[39] = commit;
    $[40] = sat;
    $[41] = val;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      background: "linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)"
    };
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== hue || $[45] !== t21) {
    t23 = <div className="px-4 pt-4 pb-3"><input type="range" aria-label="Hue" min={0} max={360} value={hue} onChange={t21} className="w-full h-3 rounded-full appearance-none cursor-pointer" style={t22} /></div>;
    $[44] = hue;
    $[45] = t21;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span className="px-3 py-2 text-2xs text-fg-muted border-r border-border bg-surface-subtle select-none">HEX</span>;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== onChange) {
    t25 = event_5 => {
      const raw = event_5.target.value;
      setHexInput(raw);
      const hex_0 = raw.startsWith("#") ? raw : `#${raw}`;
      if (isValidHex(hex_0)) {
        const hsv = hexToHsv(hex_0);
        setHue(hsv.h);
        setSat(hsv.s);
        setVal(hsv.v);
        onChange(hex_0);
      }
    };
    $[48] = onChange;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== hexInput || $[51] !== t25) {
    t26 = <HStack align="center" className="flex-1 border border-border rounded-lg overflow-hidden">{t24}<input type="text" aria-label="Hex colour value" value={hexInput} onChange={t25} maxLength={7} className="flex-1 px-3 py-2 text-2xs font-mono text-fg bg-surface focus:outline-none" spellCheck={false} /></HStack>;
    $[50] = hexInput;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <button type="button" className="size-9 rounded-lg border border-border flex items-center justify-center hover:bg-surface-muted transition-colors" aria-label="Eyedropper"><Pipette className="size-4 text-fg-muted" strokeWidth={1.5} /></button>;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t26) {
    t28 = <HStack align="center" gap={2} className="px-4 pb-4">{t26}{t27}</HStack>;
    $[54] = t26;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t20 || $[57] !== t23 || $[58] !== t28 || $[59] !== t9) {
    t29 = <div ref={popoverRef} className="absolute left-0 top-full mt-2 z-50 bg-surface rounded-xl shadow-2xl border border-border w-80 overflow-hidden">{t9}{t20}{t23}{t28}</div>;
    $[56] = t20;
    $[57] = t23;
    $[58] = t28;
    $[59] = t9;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  return t29;
};